import { useState, useEffect, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import TopBar from './Topbar';
import Sidebar from './Sidebar';
import AIAssistantSidebar from '../AIAssistantSidebar';
import TextingPanel from '../TextingPanel';
import RootErrorBoundary from '../RootErrorBoundary';
import PageSpinner from '../PageSpinner';
import DemoBanner from '../DemoBanner';
import ViewAsRoleBanner from '../admin/ViewAsRoleBanner';
import UpdateBanner from '../UpdateBanner';
import OnboardingWalkthrough from '../Onboarding/OnboardingWalkthrough';
import CommandPalette from '../CommandPalette';
import { SkipToMain } from '../States';
import { usePageTitle } from '../../lib/pageTitle';
import Icon from '../ui/Icon';

export default function AppShell() {
  // Per-route browser tab titles (see lib/pageTitle.ts).
  usePageTitle();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // Desktop sidebar collapse — gives the main panel the full window
  // width when the user wants to read a long page or view a wide table.
  // Persists across page loads so reload doesn't re-open it.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('fnsai.sidebar.collapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('fnsai.sidebar.collapsed', sidebarCollapsed ? '1' : '0'); } catch { /* private mode */ }
  }, [sidebarCollapsed]);

  useEffect(() => {
    const handler = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Close sidebar when navigating on mobile
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [window.location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`app-shell${!isMobile && sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
      {/* Keyboard accessibility — first focusable element, hidden until tabbed */}
      <SkipToMain />

      {/* Phase 8 — yellow banner when admin is simulating another role */}
      <ViewAsRoleBanner />

      {/* Offers a refresh when the app has been redeployed since this tab
          loaded. Prompts rather than reloading — nobody should lose a
          half-written screening note to a deploy. */}
      <UpdateBanner />

      {/* First-time user welcome tour. Dismissable, only shows once per user. */}
      <OnboardingWalkthrough />

      {/* Cmd/Ctrl+K command palette — works everywhere, permission-aware. */}
      <CommandPalette />

      <TopBar onMenuClick={() => setSidebarOpen(v => !v)} showMenuButton={isMobile} />

      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 199 }}
        />
      )}

      <Sidebar
        isOpen={!isMobile ? !sidebarCollapsed : sidebarOpen}
        isMobile={isMobile}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Floating pill toggle pinned to the very bottom-left, sitting
          right above where the "View as…" simulate button lives in the
          sidebar — so the user has one obvious spot to push the
          sidebar away and bring it back. Hidden on mobile (mobile uses
          the top-bar burger). */}
      {!isMobile && (
        <button
          type="button"
          onClick={() => setSidebarCollapsed((v) => !v)}
          title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
          aria-label={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
          style={{ // Sit to the right of the "View as…" simulate button
            // (which is fixed at left:16, ~110 px wide). The toggle still
            // shows even when "View as" isn't rendered (non-admin) —
            // it'll just have empty space to its left.
            position: 'fixed', left: 140, bottom: 16, zIndex: 350, padding: '8px 14px', borderRadius: 999, border: '1px solid var(--text-strong)', background: 'var(--text-strong)', color: 'var(--surface)', fontSize: 12, fontWeight: 700, cursor: 'pointer', boxShadow: '0 6px 20px rgba(15, 23, 42, 0.25)', display: 'flex', alignItems: 'center', gap: 6, letterSpacing: '0.02em' }}
        >
          <span style={{ display: 'inline-flex' }}><Icon name={sidebarCollapsed ? 'menu' : 'chevron-left'} size={14} /></span>
          <span>{sidebarCollapsed ? 'Show sidebar' : 'Hide'}</span>
        </button>
      )}

      <main
        id="main-content"
        className="main-content"
        style={
          isMobile
            ? { marginLeft: 0, paddingTop: 60 }
            : sidebarCollapsed
              ? { marginLeft: 0 }
              : undefined
        }
      >
        {/* Phase 1 QA fix — wrap the routed page in an error boundary so a
            crash in one page (e.g. Reminders) doesn't blank the sidebar +
            topbar + whole shell. User can still navigate. */}
        <DemoBanner />
        <RootErrorBoundary>
          {/* This Suspense boundary is the fix for the spinner blocking the
              whole page. App.tsx's outer <Suspense> wraps the entire
              <Routes> tree — including this AppShell — so when a lazy-loaded
              page was navigated to, React had to unmount everything under
              that boundary (topbar, sidebar, all of it) and render just the
              spinner in their place. Since this inner boundary sits closer
              to <Outlet/>, React satisfies it here instead: the lazy page
              component's suspend is caught right here, so only the content
              column swaps to PageSpinner while TopBar/Sidebar above stay
              mounted. The outer Suspense in App.tsx still does its job for
              routes that render with no shell at all (the public token-auth
              pages — /sign, /careers, etc. — which have nothing to preserve
              during their own load). */}
          <Suspense fallback={<PageSpinner />}>
            <Outlet />
          </Suspense>
        </RootErrorBoundary>
      </main>

      {/* Global AI Brain sidebar — mounts once, available on every page,
          auto-detects the route's entity (candidate/job/client/etc.) */}
      <AIAssistantSidebar />

      {/* Phase 1.1C — Global texting panel. Floating 💬 button bottom-right,
          below the AI button. Opens a slide-out panel with candidate
          picker + message box. Can also be opened programmatically from
          any page via window.dispatchEvent(new CustomEvent('open-texting-panel', { detail: { candidateId } })) */}
      <TextingPanel />
    </div>
  );
}
